/**
 * ProjectConfigPanel — the admin project detail's "Configuration" card
 * (mockup `#s-detail` Configuration card): an editable golden-GT-annotator
 * field (`useSetGtAnnotatorMutation`, save on click), an editable
 * email-recipients token list (add/remove, `useSetEmailRecipientsMutation`
 * per change), and a READ-ONLY display of the project's demo/working batch
 * shapes + gates — those are fixed at registration (`RegisterProjectModal`)
 * and this panel does not offer a way to change them, matching the mockup's
 * own "Batch shapes & gates are set at registration — read-only here." note.
 *
 * Replaces the read-only Configuration block previously inlined in
 * `AdminProjectDetail.tsx` (Task 8's `TODO(config)` stub).
 */

import { useState } from "react";

import { useLabelingRole } from "@/hooks/useLabelingRole";
import { Button, Input } from "@/design-system/components/react";
import {
  useSetEmailRecipientsMutation,
  useSetGtAnnotatorMutation,
  useSetGtReferenceMutation,
} from "../../../../hooks/useFactoryQueries";
import type { Project } from "../../../../types/factory";
import { logger } from "../../../../utils/logger";
import { gatedBoundaryLabels } from "./PipelineStrip";

/** "demo→P1, P1→P2 · then auto from phase 2" (or whatever boundaries are
 *  actually gated for this project's floor) — derived from the same
 *  `gatedBoundaryLabels` PipelineStrip uses for its own gate markers, so
 *  this copy can't drift out of sync with the real gate placement the way
 *  the previous hardcoded "demo→P1, P1→P2" string did. */
function gatesDescription(autoAdvanceFromPhase: number): string {
  const labels = gatedBoundaryLabels(autoAdvanceFromPhase);
  const prefix = labels.length > 0 ? `${labels.join(", ")} · then ` : "";
  return `${prefix}auto from phase ${autoAdvanceFromPhase}`;
}

export interface ProjectConfigPanelProps {
  project: Project;
}

export function ProjectConfigPanel({ project }: ProjectConfigPanelProps) {
  const role = useLabelingRole();
  const [gtAnnotatorInput, setGtAnnotatorInput] = useState(project.gt_annotator ?? "");
  const [gtReferenceInput, setGtReferenceInput] = useState(
    project.gt_worklist_project_id ?? `${project.project_id}-gt-shared`,
  );
  const [newRecipient, setNewRecipient] = useState("");

  const gtMutation = useSetGtAnnotatorMutation();
  const gtReferenceMutation = useSetGtReferenceMutation();
  const recipientsMutation = useSetEmailRecipientsMutation();

  const gtDirty = gtAnnotatorInput.trim() !== (project.gt_annotator ?? "");
  const gtReferenceDirty =
    gtReferenceInput.trim() !==
    (project.gt_worklist_project_id ?? `${project.project_id}-gt-shared`);

  const handleSaveGtAnnotator = () => {
    gtMutation.mutate(
      { pid: project.project_id, gt_annotator: gtAnnotatorInput.trim() },
      {
        onError: (err: unknown) =>
          logger.error("[ProjectConfigPanel] set gt-annotator failed", err),
      },
    );
  };

  const handleSaveGtReference = () => {
    gtReferenceMutation.mutate(
      { pid: project.project_id, worklist_project_id: gtReferenceInput.trim() },
      {
        onError: (err: unknown) =>
          logger.error("[ProjectConfigPanel] set gt-reference failed", err),
      },
    );
  };

  const saveRecipients = (recipients: string[]) => {
    recipientsMutation.mutate(
      { pid: project.project_id, recipients },
      {
        onError: (err: unknown) =>
          logger.error("[ProjectConfigPanel] set email-recipients failed", err),
      },
    );
  };

  const handleAddRecipient = () => {
    const email = newRecipient.trim();
    setNewRecipient("");
    if (!email || project.email_recipients.includes(email)) return;
    saveRecipients([...project.email_recipients, email]);
  };

  const handleRemoveRecipient = (email: string) => {
    saveRecipients(project.email_recipients.filter((e) => e !== email));
  };

  return (
    <div className="rounded border border-border/50 bg-surface p-5">
      <h3 className="mb-3 text-sm font-semibold text-fg">Configuration</h3>
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="cfg-gt-annotator"
            className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-fg-dim"
          >
            Golden-GT annotator
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="cfg-gt-annotator"
              className="font-mono text-xs"
              placeholder="rad.gt@a2zradiology.ai"
              value={gtAnnotatorInput}
              onChange={(e) => setGtAnnotatorInput(e.target.value)}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSaveGtAnnotator}
              disabled={!gtDirty || gtMutation.isPending}
            >
              Save
            </Button>
          </div>
          <span className="text-[11px] text-fg-faint">
            Scores every annotator&apos;s demo against this reference.
          </span>
          {gtMutation.isError && (
            <span className="text-[11px] text-warn">Failed to save — retry.</span>
          )}
        </div>

        {role === "admin" && (
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="cfg-gt-reference"
              className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-fg-dim"
            >
              Ground-truth worklist
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="cfg-gt-reference"
                className="font-mono text-xs"
                placeholder={`${project.project_id}-gt-shared`}
                value={gtReferenceInput}
                onChange={(e) => setGtReferenceInput(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSaveGtReference}
                disabled={
                  !gtReferenceDirty || !gtReferenceInput.trim() || gtReferenceMutation.isPending
                }
              >
                Save
              </Button>
            </div>
            <span className="text-[11px] text-fg-faint">
              Masks in this worklist are the comparison reference for demo evaluations.
            </span>
            {gtReferenceMutation.isError && (
              <span className="text-[11px] text-warn">Failed to save — retry.</span>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-fg-dim">
            Email recipients
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {project.email_recipients.length === 0 && (
              <span className="text-xs text-fg-faint">None configured</span>
            )}
            {project.email_recipients.map((email) => (
              <span
                key={email}
                className="flex items-center gap-1.5 rounded border border-border-strong bg-surface-2 px-2 py-0.5 font-mono text-[11px] text-fg-muted"
              >
                {email}
                <button
                  type="button"
                  onClick={() => handleRemoveRecipient(email)}
                  aria-label={`Remove ${email}`}
                  className="text-fg-faint transition hover:text-warn"
                >
                  ✕
                </button>
              </span>
            ))}
            <Input
              className="h-7 w-44 font-mono text-[11px]"
              placeholder="add@a2zradiology.ai"
              value={newRecipient}
              onChange={(e) => setNewRecipient(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddRecipient();
                }
              }}
              aria-label="Add email recipient"
            />
            <button
              type="button"
              onClick={handleAddRecipient}
              className="text-xs font-semibold text-primary transition hover:text-primary/80"
            >
              ＋ add
            </button>
          </div>
          <span className="text-[11px] text-fg-faint">
            Notified on every batch completion + pool-empty.
          </span>
          {recipientsMutation.isError && (
            <span className="text-[11px] text-warn">Failed to save — retry.</span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-border/50 pt-3 text-xs">
          <span className="font-mono uppercase tracking-[0.08em] text-fg-dim">Demo</span>
          <span className="font-mono text-fg-muted">
            {project.phase_config.demo_total} · {project.phase_config.demo_unique}+
            {project.phase_config.demo_gt} GT
          </span>
          <span className="font-mono uppercase tracking-[0.08em] text-fg-dim">Working</span>
          <span className="font-mono text-fg-muted">
            {project.phase_config.working_total} · {project.phase_config.working_unique}+
            {project.phase_config.working_overlap} overlap
          </span>
          <span className="font-mono uppercase tracking-[0.08em] text-fg-dim">Gates</span>
          <span className="font-mono text-fg-muted">
            {gatesDescription(project.gate_policy.auto_advance_from_phase)}
          </span>
        </div>
        <div className="text-[11px] text-fg-faint">
          Batch shapes &amp; gates are set at registration — read-only here.
        </div>
      </div>
    </div>
  );
}

export default ProjectConfigPanel;
